import React, {useState, Fragment} from 'react'
import {connect} from 'react-redux'
import {Link} from 'react-router'
import styles from './CollectionListing.scss'
import {CollectionItems} from './CollectionItems'
import {CollectionMap} from './CollectionMap'
import {collectionsMessages} from 'features/homepage/Collections/intlMessages'
import {LoadExistingContainer} from 'features/search/LoadExisting'
import {collectionActions, collectionSelectors} from 'data/resources/collections'
import {collectionItemActions, collectionItemSelectors} from 'data/resources/collectionItems'
import {collectionFollowerActions, collectionFollowerSelectors} from 'data/resources/collectionFollowers'
import {userSelectors} from 'data/resources/users'
import {routeSelectors} from 'data/resources/routes'
import {tripSelectors} from 'data/resources/trips'
import {ColWrap, MainCol, SecondaryCol} from 'common/layouts/StandardLayout/TwoCol'
import {StandardLayout} from 'common/layouts/StandardLayout'
import {withCollapse} from 'common/layouts/ThreePanelLayout/ThreePanelCollapseContext'
import {Topo} from 'common/styleguide/Topo'
import {Card} from 'common/styleguide/Card'
import {CTA} from 'common/styleguide/CTA'
import {Loadable} from 'common/Loadable'
import {Icon} from 'common/Icon'
import {DashedMetric} from 'common/DashedMetric'
import {Distance, Elevation} from 'common/units'
import {CREATE_EDIT_COLLECTION_DIALOG} from 'common/modals/CreateEditCollection'
import {DELETE_RECORD_MODAL} from 'common/modals/DeleteRecordModal'
import {SHARE_MODAL} from 'common/modals/ShareModal'
import {loadGeoJSON} from 'lib/api/embeds/regional'
import {showModal} from 'lib/modalUtils'
import {missingPhoto} from 'lib/photoUtils'
import {Msg, commonNouns, commonVerbs} from 'lib/localization'
import {FormattedMessage as T, injectIntl} from 'react-intl'
import {Set} from 'immutable'
import cx from 'classnames'
import _ from 'lodash'

const itemTypes = (items) => {
  return Set(items.valueSeq().map(i => i.itemType)).toJS()
}

export class _CollectionListing extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      allOverview: false,
      showImport: false,
      pendingAdditions: Set(),
      savePending: false,
      saveSuccess: false
    }
  }

  toggleAllOverview = () => {
    this.setState({allOverview: !this.state.allOverview})
  }

  determineMainHeader = () => {
    const types = itemTypes(this.props.collectionItems)
    if (types.length === 1) {
      return `Collection ${_.capitalize(types[0])}s`
    } else {
      return 'Collection Items'
    }
  }

  addTRoutes = (items) => {
    const {collection, batchAddCollectionItems} = this.props
    batchAddCollectionItems({
      collectionId: collection.id,
      items: items.map(([id, type]) => ({item_type: _.capitalize(type), item_id: id})),
      extras: 'troutes'
    })
  }

  importClicked = () => {
    this.setState({showImport: true})
  }

  toggleFollow = (currentlyFollowing) => {
    const {collection, createCollectionFollower, destroyCollectionFollower, collectionFollower, currentUser} = this.props

    if (!currentUser.id) {
      window.location.href = '/signup'
      return
    }

    currentlyFollowing
      ? destroyCollectionFollower(collectionFollower(currentUser.id))
      : createCollectionFollower(collection.id, collection.privacyCode)
  }

  render () {
    const {
      collection: c,
      collectionTRoutes,
      collectionItems,
      collectionFollower,
      user,
      currentUser,
      collapse,
      intl
    } = this.props
    if (!c) {
      // This situation can occur momentarily on collection deletion, after the collection
      // has been deleted but before browserHistory.replace('/') call takes effect.
      // We need to handle it nicely or the error will trigger our errorbound UI
      return null
    }
    const {showImport} = this.state
    const currentUserIsOwner = c.userId === currentUser.id
    const mainHeader = this.determineMainHeader()
    const showMap = c.showMap && collectionTRoutes.size > 0
    const currentUserIsFollowing = collectionFollower(currentUser.id)
    const header = title => collapse ? <h3>{title}</h3> : <h1>{title}</h1>

    return (
      <StandardLayout greyBg>
        {showImport && <LoadExistingContainer
          loadMultiple={this.addTRoutes}
          collapse={collapse}
          onRequestClose={() => this.setState({showImport: false})}
          submitLabel={collapse ? <Msg m={commonVerbs.add} /> : <Msg m={collectionsMessages.addToCollection} />} />}

        {currentUserIsOwner && <Toolbar c={c} importClicked={this.importClicked} collapse={collapse} />}

        <Topo dark className={styles.banner}>
          {!missingPhoto(c.cover) && <img key={c.updatedAt} src={c.cover} className={styles.bannerImg} />}
          <div className={styles.bannerText}>
            {header(c.title)}
            {!currentUserIsOwner && <div className={styles.byline}>By {<Link to={'/users/' + user().id}>{user().name}</Link>}</div>}
            {currentUserIsOwner
              ? <CTA style={{width: '120px'}} className={collapse ? styles.collapse : null} onClick={() => showModal(SHARE_MODAL, {
                objectTitle: c.title,
                objectUrl: `/collections/${c.id}${c.privacyCode && '?privacy_code=' + c.privacyCode}`
              })}>Share</CTA>
              : <CTA style={{width: '200px'}} className={collapse ? styles.collapse : null} onClick={() => this.toggleFollow(currentUserIsFollowing)}>
                {currentUserIsFollowing ? 'Unfollow' : 'Follow'} Collection
              </CTA>}
          </div>
        </Topo>

        <ColWrap>
          <MainCol>
            {collapse && c.description && <Overview c={c} collapse={collapse} />}

            {showMap && <Card noPadding className={cx(styles.collectionMap, collapse && styles.collapse)}>
              <CollectionMap
                collapse={collapse}
                collectionId={c.id}
                updatedAt={c.updatedAt} // this is supposed to be rso.updatedAt, but for collections, rso.updatedAt should === c.updatedAt
              />
            </Card>}

            {collapse && <Stats items={collectionTRoutes} />}

            <h2 data-heading-alt>{mainHeader}</h2>

            <CollectionItems
              intl={intl}
              collection={c}
              collapse={collapse}
              troutes={collectionTRoutes}
              itemTypes={itemTypes(collectionItems)}
              addTRoutes={this.addTRoutes}
              currentUserIsOwner={currentUserIsOwner} />
          </MainCol>

          {!collapse && <SecondaryCol>
            {c.description && <Overview c={c} />}

            <Stats items={collectionTRoutes} />
          </SecondaryCol>}
        </ColWrap>
      </StandardLayout>
    )
  }
}

const Toolbar = ({c, importClicked, collapse}) => <div className={cx(styles.toolbar, collapse && styles.collapse)}>
  <div className={styles.leftButtons}>
    <div className={styles.btn} onClick={() => importClicked()}><Icon size={18} i='save-alt' /><Msg m={collapse ? commonVerbs.import : collectionsMessages.importRoutesAndRides} /></div>
    <div className={styles.btn} onClick={() => showModal(CREATE_EDIT_COLLECTION_DIALOG, {
      modalTitle: <Msg m={collectionsMessages.editCollection} />,
      collection: c
    })}>
      <Icon size={18} i='md-create' /><Msg m={collapse ? commonVerbs.edit : collectionsMessages.editCollection} />
    </div>
  </div>

  <div className={styles.deleteButton} onClick={() => showModal(DELETE_RECORD_MODAL, {
    objectType: 'collection',
    objectName: c.title,
    object: c,
    successPath: '/collections'
  })}>
    <Icon size={18} i='md-trash' /><Msg m={commonVerbs.delete} />
  </div>
</div>

const Overview = ({c, collapse}) => {
  const [allOverview, setAllOverview] = useState(false)
  const length = collapse ? 200 : 350

  return <Fragment>
    <h2 data-heading-alt><Msg m={commonNouns.overview} /></h2>
    <Card>
      <div className={styles.overviewText}>
        {allOverview
          ? c.description
          : _.truncate(c.description, {length})}
      </div>
      {c.description.length > length && <a data-card-link onClick={() => setAllOverview(!allOverview)}>
        {allOverview
          ? <Msg m={collectionsMessages.hideMore} />
          : <Msg m={collectionsMessages.showMore} />}
      </a>}
    </Card>
  </Fragment>
}

const Stats = ({items}) => {
  const ary = items.toArray()
  return <Fragment>
    <h2 data-heading-alt><Msg m={commonNouns.stats} /></h2>
    <Card className={styles.statsCard}>
      <Distance value={_.sumBy(ary, 'distance')}>
        {m => <DashedMetric
          label={m.long}
          value={m.valueToString()} />}
      </Distance>

      <Elevation value={_.sumBy(ary, 'elevationGain')}>
        {m => <DashedMetric
          label={m.long}
          value={m.valueToString()} />}
      </Elevation>

      <DashedMetric
        noMargin
        label={<T
          id='CollectionListing.Stats.itemsLabel'
          defaultMessage='collection items'
          description='A label denoting the number of items in a collection' />}
        value={items.size} />
    </Card>
  </Fragment>
}

const CollapsibleCollectionListing = withCollapse(_CollectionListing)
const I18nCollectionListing = injectIntl(CollapsibleCollectionListing)

const loadInitialResources = ({loadCollection, params}) => {
  const collectionId = parseInt(params.id, 10)

  return loadCollection(collectionId)
}

const LoadableCollectionListing = (props) => {
  return <Loadable
    {...props}
    component={I18nCollectionListing}
    loadingText={<T id='Collections.loadableText' defaultMessage='Loading collection' />}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  const collectionId = parseInt(ownProps.params.id, 10)

  return {
    currentUser: state.currentUser,
    collection: collectionSelectors.find(state, collectionId),
    collectionItems: collectionItemSelectors
      .byCollectionId(state, collectionId),
    collectionItem: (id, type) => collectionItemSelectors.byItem(state, id, type),
    collectionTRoutes: collectionItemSelectors
      .byCollectionId(state, collectionId)
      .map(i => i.itemType === 'route'
        ? routeSelectors.find(state, i.itemId)
        : tripSelectors.find(state, i.itemId)),
    collectionFollower: userId => collectionFollowerSelectors.byCollectionAndUser(state, collectionId, userId),
    user: () => userSelectors.find(state, collectionSelectors.find(state, collectionId).userId)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const privacyCode = ownProps.location.query && ownProps.location.query.privacy_code

  return {
    loadCollection: id => dispatch(collectionActions.read(id, {privacyCode, extras: 'collection_items,troutes,user,current_user_follower,route_set_overview'})),
    loadCollectionFollower: (collectionId, userId) => dispatch(collectionFollowerActions.list({collectionId, userId})),
    batchAddCollectionItems: params => dispatch(collectionItemActions.batchAdd(params)),
    createCollectionFollower: (collectionId, privacyCode) => dispatch(collectionFollowerActions.create({collectionId, privacyCode})),
    loadGeoJSON: (id, updatedAt) => dispatch(loadGeoJSON(id, updatedAt))
  }
}

export const CollectionListing = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableCollectionListing)
